import { motion } from 'framer-motion';

import { useStrings } from '../../hooks/useStrings';
import type { DailyRingsState } from '../../stores/progress.store';

interface PleasureRingsProps {
  rings: DailyRingsState;
  size?: number;
}

interface RingSpec {
  key: 'mind' | 'muscle' | 'sensation';
  radius: number;
  color: string; // hsl(var(...))
  filterId: string;
}

/**
 * Three concentric daily progress rings — outer = Mind (Level 1 breath),
 * middle = Muscle (Level 2 pelvic power), inner = Sensation (Level 3 audio).
 * Filling all three closes the day. Animates via stroke-dashoffset.
 */
export function PleasureRings({ rings, size = 220 }: PleasureRingsProps) {
  const { strings } = useStrings();
  const cx = size / 2;
  const cy = size / 2;

  const ringSpecs: readonly RingSpec[] = [
    { key: 'mind', radius: size * 0.42, color: 'hsl(var(--secondary))', filterId: 'pr-glow-mind' },
    { key: 'muscle', radius: size * 0.32, color: 'hsl(var(--primary))', filterId: 'pr-glow-muscle' },
    {
      key: 'sensation',
      radius: size * 0.22,
      color: 'hsl(var(--accent))',
      filterId: 'pr-glow-sensation',
    },
  ];

  const allClosed =
    rings.mind >= rings.target.mind &&
    rings.muscle >= rings.target.muscle &&
    rings.sensation >= rings.target.sensation;

  return (
    <div className="mx-auto flex flex-col items-center gap-3">
      <p className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground">
        {strings.yourSpace.pleasureRings.eyebrow}
      </p>

      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
          <defs>
            {ringSpecs.map((r) => (
              <filter
                key={r.filterId}
                id={r.filterId}
                x="-50%"
                y="-50%"
                width="200%"
                height="200%"
              >
                <feGaussianBlur stdDeviation="2.5" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            ))}
          </defs>

          {ringSpecs.map((r) => {
            const value = rings[r.key];
            const target = rings.target[r.key];
            const progress = target > 0 ? Math.min(value / target, 1) : 0;
            const circumference = 2 * Math.PI * r.radius;
            const closed = progress >= 1;
            return (
              <g key={r.key}>
                <circle
                  cx={cx}
                  cy={cy}
                  r={r.radius}
                  fill="none"
                  stroke={r.color}
                  strokeOpacity={0.16}
                  strokeWidth={8}
                />
                <motion.circle
                  cx={cx}
                  cy={cy}
                  r={r.radius}
                  fill="none"
                  stroke={r.color}
                  strokeWidth={8}
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  initial={{ strokeDashoffset: circumference }}
                  animate={{
                    strokeDashoffset: circumference * (1 - progress),
                    opacity: closed ? [1, 0.7, 1] : 1,
                  }}
                  transition={{
                    strokeDashoffset: { duration: 0.9, ease: 'easeOut' },
                    opacity: closed
                      ? { duration: 2.8, repeat: Infinity, ease: 'easeInOut' }
                      : { duration: 0.4 },
                  }}
                  style={{
                    transform: `rotate(-90deg)`,
                    transformOrigin: `${cx}px ${cy}px`,
                    filter: closed ? `url(#${r.filterId})` : undefined,
                  }}
                />
              </g>
            );
          })}

          {allClosed && (
            <motion.circle
              cx={cx}
              cy={cy}
              r={size * 0.08}
              fill="hsl(var(--accent) / 0.8)"
              initial={{ scale: 0 }}
              animate={{ scale: [0.9, 1.05, 0.9], opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 3.4, repeat: Infinity, ease: 'easeInOut' }}
              style={{ transformOrigin: `${cx}px ${cy}px` }}
            />
          )}
        </svg>

        {allClosed && (
          <p
            aria-live="polite"
            className="pointer-events-none absolute inset-x-0 -bottom-2 text-center text-[10px] uppercase tracking-[0.22em] text-accent"
          >
            {strings.yourSpace.pleasureRings.allClosed}
          </p>
        )}
      </div>

      <div className="flex gap-3 text-[10px] uppercase tracking-[0.18em]">
        <span style={{ color: 'hsl(var(--secondary))' }}>
          {strings.yourSpace.pleasureRings.mindLabel}
        </span>
        <span className="text-muted-foreground" aria-hidden>
          ·
        </span>
        <span style={{ color: 'hsl(var(--primary))' }}>
          {strings.yourSpace.pleasureRings.muscleLabel}
        </span>
        <span className="text-muted-foreground" aria-hidden>
          ·
        </span>
        <span style={{ color: 'hsl(var(--accent))' }}>
          {strings.yourSpace.pleasureRings.sensationLabel}
        </span>
      </div>
    </div>
  );
}
